import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Dumbbell, Target, Users, Award, Calendar, ArrowRight, CheckCircle, Star } from 'lucide-react';
import { useState, useEffect } from 'react';
import SplitText from '../components/SplitText';

/**
 * Home Page Component
 * Main landing page for LC Performance featuring hero, features, stats, testimonials, and CTA
 */
export default function Home() {
  return (
    <div className="min-h-screen">
      <HeroSection />
      <StatsSection />
      <FeaturesSection />
      <TestimonialsSection />
      <CTASection />
    </div>
  );
}

// Hero Section - Fitness-focused with strong CTA
function HeroSection() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-primary-700 via-primary-600 to-accent-500">
      {/* Animated Background Pattern */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-white rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
        <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-accent-300 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-1/4 left-1/3 w-96 h-96 bg-primary-300 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
            <Award className="w-4 h-4 text-white" />
            <span className="text-sm font-semibold text-white">Elite Personal Training & Lifestyle Coaching</span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-display font-bold text-white mb-6 leading-tight">
            <SplitText 
              lines={[
                "Transform Your Body,",
                "Elevate Your Life"
              ]}
              className="block w-full"
            />
          </h1>

          <p className="text-xl md:text-2xl text-white/90 mb-10 max-w-3xl mx-auto leading-relaxed">
            Expert 1-on-1 coaching and online training programs from Luke Crisanti.
            Personalized guidance, daily support, and proven results in Palm Beach and beyond.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/services"
              className="px-8 py-4 bg-white text-primary-600 rounded-full font-bold text-lg shadow-2xl hover:shadow-3xl hover:scale-105 transform transition-all duration-300 inline-flex items-center gap-2"
            >
              View Programs
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/contact"
              className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white border-2 border-white rounded-full font-bold text-lg hover:bg-white/20 transition-all duration-300 inline-flex items-center gap-2"
            >
              <Calendar className="w-5 h-5" />
              Book Free Consultation
            </Link>
          </div>

          {/* Trust Indicators */}
          <div className="mt-16 flex flex-wrap justify-center gap-8 text-white/80">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-accent-200" />
              <span className="text-sm font-medium">Certified Coach</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-accent-200" />
              <span className="text-sm font-medium">500+ Transformations</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-accent-200" />
              <span className="text-sm font-medium">Online & In-Person</span>
            </div>
          </div>
        </motion.div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <svg
            className="w-6 h-6 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 14l-7 7m0 0l-7-7m7 7V3"
            />
          </svg>
        </div>
      </div>

      <style jsx>{`
        @keyframes blob {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
        }

        .animate-blob {
          animation: blob 7s infinite;
        }

        .animation-delay-2000 {
          animation-delay: 2s;
        }

        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </section>
  );
}

// Stats Section - Key metrics with animated counters
function StatsSection() {
  const stats = [
    {
      label: 'Clients Transformed',
      value: 500,
      suffix: '+',
      icon: Users,
      color: 'primary'
    },
    {
      label: 'Success Rate',
      value: 98,
      suffix: '%',
      icon: Target,
      color: 'accent'
    },
    {
      label: 'Years Experience',
      value: 10,
      suffix: '+',
      icon: Award,
      color: 'primary'
    },
    {
      label: 'Average Weight Loss',
      value: 25,
      suffix: 'lbs',
      icon: Dumbbell,
      color: 'accent'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Proven Results That Speak
          </h2>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Join hundreds of clients who have achieved their fitness goals with LC Performance
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <StatCard key={index} {...stat} delay={index * 100} />
          ))}
        </div>
      </div>
    </section>
  );
}

function StatCard({ label, value, suffix, icon: Icon, color, delay }) {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    if (hasAnimated) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setHasAnimated(true);
          const duration = 2000;
          const steps = 60;
          const increment = value / steps;
          let current = 0;

          const timer = setInterval(() => {
            current += increment;
            if (current >= value) {
              setCount(value);
              clearInterval(timer);
            } else {
              setCount(Math.floor(current));
            }
          }, duration / steps);

          return () => clearInterval(timer);
        }
      },
      { threshold: 0.5 }
    );

    const element = document.getElementById(`stat-${label}`);
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, [value, label, hasAnimated]);

  const colorClasses = {
    primary: 'from-primary-500 to-primary-600',
    accent: 'from-accent-500 to-accent-600'
  };

  return (
    <div id={`stat-${label}`} className="text-center group">
      <div className={`inline-flex items-center justify-center w-16 h-16 mb-4 rounded-2xl bg-gradient-to-br ${colorClasses[color]} transform transition-transform group-hover:scale-110`}>
        <Icon className="w-8 h-8 text-white" />
      </div>

      <div className="text-5xl font-bold mb-2 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
        {count.toLocaleString()}{suffix}
      </div>

      <div className="text-gray-400 text-lg font-medium">
        {label}
      </div>
    </div>
  );
}

// Features Section - Key benefits of training with LC Performance
function FeaturesSection() {
  const features = [
    {
      icon: <Target className="w-8 h-8" />,
      title: 'Personalized Programs',
      description: 'Custom training plans designed around your unique goals, fitness level, and lifestyle needs.'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: '1-on-1 Coaching',
      description: 'Direct access to Luke for personal training sessions, both in-person in Palm Beach and online worldwide.'
    },
    {
      icon: <Calendar className="w-8 h-8" />,
      title: 'Daily Support',
      description: 'Ongoing guidance and accountability with regular check-ins to keep you on track and motivated.'
    },
    {
      icon: <Dumbbell className="w-8 h-8" />,
      title: 'Flexible Training',
      description: 'Choose between face-to-face mentorship or online coaching that fits your schedule and location.'
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: 'Lifestyle Integration',
      description: 'Holistic approach combining fitness, nutrition, mindset, and habits for lasting transformation.'
    },
    {
      icon: <CheckCircle className="w-8 h-8" />,
      title: 'Proven Methods',
      description: 'Science-backed training techniques and nutrition strategies that deliver real, sustainable results.'
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-primary-50 px-4 py-2 rounded-full mb-4">
            <Star className="w-4 h-4 text-primary-600" />
            <span className="text-sm font-semibold text-primary-900">Why Choose LC Performance</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Elite Coaching That Gets Results
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Experience a comprehensive approach to fitness that transforms not just your body, but your entire lifestyle
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="group p-8 rounded-2xl bg-gray-50 hover:bg-gradient-to-br hover:from-primary-50 hover:to-accent-50 transition-all duration-300 hover:shadow-xl"
            >
              <div className="w-16 h-16 mb-6 flex items-center justify-center rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 text-white group-hover:scale-110 transition-transform duration-300">
                {feature.icon}
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-3">
                {feature.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Testimonials Section - Social proof
function TestimonialsSection() {
  const testimonials = [
    {
      name: 'Sarah Mitchell',
      role: 'Lost 30lbs in 4 months',
      image: '/api/placeholder/100/100',
      content: 'Luke transformed my life! His personalized approach and daily support kept me accountable. I finally achieved the body I always wanted.',
      rating: 5
    },
    {
      name: 'Mike Rodriguez',
      role: 'Gained 15lbs of muscle',
      image: '/api/placeholder/100/100',
      content: 'Working with Luke remotely was seamless. The online coaching felt just as personal as in-person training. Incredible results!',
      rating: 5
    },
    {
      name: 'Jennifer Adams',
      role: 'Marathon finisher',
      image: '/api/placeholder/100/100',
      content: 'From couch to marathon! Luke\'s holistic approach to fitness and lifestyle changed everything. Couldn\'t have done it without him.',
      rating: 5
    }
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Success Stories
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Real transformations from real clients who committed to the journey
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-shadow duration-300"
            >
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-accent-500 text-accent-500" />
                ))}
              </div>

              <p className="text-gray-700 mb-6 leading-relaxed italic">
                "{testimonial.content}"
              </p>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center text-white font-bold">
                  {testimonial.name.charAt(0)}
                </div>
                <div>
                  <div className="font-bold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-600">{testimonial.role}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// CTA Section - Final conversion push
function CTASection() {
  return (
    <section className="py-20 bg-gradient-to-r from-primary-700 to-primary-600">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Start Your Transformation?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Join the LC Performance community and get personalized training that delivers real results.
            Book your free consultation today and take the first step.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/contact"
              className="px-8 py-4 bg-white text-primary-600 rounded-full font-bold text-lg shadow-2xl hover:shadow-3xl hover:scale-105 transform transition-all duration-300 inline-flex items-center gap-2"
            >
              <Calendar className="w-5 h-5" />
              Book Free Consultation
            </Link>
            <Link
              to="/services"
              className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white border-2 border-white rounded-full font-bold text-lg hover:bg-white/20 transition-all duration-300"
            >
              View Programs
            </Link>
          </div>

          <p className="mt-6 text-white/70 text-sm">
            No pressure, no commitment - just a genuine conversation about your goals
          </p>
        </motion.div>
      </div>
    </section>
  );
}
